import { useState, useEffect, useCallback } from "react";
import { MagnifyingGlassIcon, XMarkIcon, BookOpenIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import {
  LibraryBrowserProvider,
  useLibraryBrowser,
  type LibraryBrowserProviderProps,
  type LibrarySection,
} from "./LibraryBrowserContext";
import Sidebar from "./Sidebar";
import Breadcrumb from "./Breadcrumb";
import ContentArea from "./ContentArea";
import DetailPane, { type DetailPaneCallbacks } from "./DetailPane";
import { useDensity } from "@/contexts/DensityContext";

// ── Public API ──────────────────────────────────────────────────────────

export interface LibraryBrowserProps extends Omit<LibraryBrowserProviderProps, 'children'> {
  detailCallbacks?: DetailPaneCallbacks;
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  sidebarTitle?: string;
  customRightPane?: React.ReactNode;
  contentHeader?: React.ReactNode;
}

export default function LibraryBrowser({
  detailCallbacks,
  className,
  header,
  footer,
  sidebarTitle,
  customRightPane,
  contentHeader,
  ...providerProps
}: LibraryBrowserProps) {
  return (
    <LibraryBrowserProvider {...providerProps}>
      <LibraryBrowserInner
        detailCallbacks={detailCallbacks}
        className={className}
        header={header}
        footer={footer}
        sidebarTitle={sidebarTitle}
        customRightPane={customRightPane}
        contentHeader={contentHeader}
      />
    </LibraryBrowserProvider>
  );
}

// ── Responsive breakpoints ──────────────────────────────────────────────

type ScreenSize = "mobile" | "tablet" | "desktop";

function useScreenSize(): ScreenSize {
  const [size, setSize] = useState<ScreenSize>(() => {
    if (typeof window === "undefined") return "desktop";
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1280) return "tablet";
    return "desktop";
  });

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 768) setSize("mobile");
      else if (window.innerWidth < 1280) setSize("tablet");
      else setSize("desktop");
    };

    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return size;
}

// ── Inner (needs context) ───────────────────────────────────────────────

interface InnerProps {
  detailCallbacks?: DetailPaneCallbacks;
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  sidebarTitle?: string;
  customRightPane?: React.ReactNode;
  contentHeader?: React.ReactNode;
}

function LibraryBrowserInner({ detailCallbacks, className, header, footer, sidebarTitle, customRightPane, contentHeader }: InnerProps) {
  const { searchQuery, setSearchQuery, selectedItem, mode, sections, activePath, navigateTo } =
    useLibraryBrowser();
  const screenSize = useScreenSize();
  const { isCompact } = useDensity();

  const isMobile = screenSize === "mobile";
  const isTablet = screenSize === "tablet";
  const showSidebar = !isMobile;
  const showRightPane = !isMobile;

  return (
    <div
      className={clsx(
        "flex flex-col h-full bg-gray-800 rounded-lg overflow-hidden border border-gray-700/50",
        className
      )}
    >
      {/* Optional header */}
      {header}

      {/* Main pane area */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar — hidden on mobile */}
        {showSidebar && (
          <div className={clsx(
            "flex-shrink-0",
            isTablet ? "w-52" : "w-60 xl:w-64"
          )}>
            <Sidebar title={sidebarTitle} />
          </div>
        )}

        {/* Center pane */}
        <div className={clsx(
          "flex flex-col min-w-0 overflow-hidden",
          mode === "builder" ? "w-[400px] flex-shrink-0" : "flex-1",
          showSidebar && "border-l border-gray-700/50"
        )}>
          {/* Content header slot */}
          {contentHeader}

          {/* Mobile section selector — replaces sidebar on mobile */}
          {isMobile && (
            <MobileSectionSelector
              sections={sections}
              activePath={activePath}
              navigateTo={navigateTo}
            />
          )}

          {/* Breadcrumb — hidden on mobile and in builder mode */}
          {!isMobile && mode !== "builder" && <Breadcrumb />}

          {/* Search bar */}
          <div className={clsx("px-3 border-b border-gray-700/30", isCompact ? "py-1.5" : "py-2")}>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search watchlists, filters..."
                className="w-full pl-8 pr-8 py-1.5 text-xs bg-gray-900 text-gray-200 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="size-3" />
                </button>
              )}
            </div>
          </div>

          {/* Content area */}
          <ContentArea />
        </div>

        {/* Right pane — hidden on mobile */}
        {showRightPane && (
          <>
            {customRightPane ? (
              <div className={clsx(
                mode === "builder" ? "flex-1 min-w-0" : clsx("flex-shrink-0", isTablet ? "w-[400px]" : "w-[560px] xl:w-[640px]")
              )}>
                {customRightPane}
              </div>
            ) : selectedItem ? (
              <div className={clsx("flex-shrink-0", isTablet ? "w-[400px]" : "w-[560px] xl:w-[640px]")}>
                <DetailPane callbacks={detailCallbacks} />
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Mobile detail sheet — slides up when item selected */}
      {isMobile && selectedItem && !customRightPane && (
        <MobileDetailSheet detailCallbacks={detailCallbacks} />
      )}

      {/* Optional footer */}
      {footer && (
        <div className={clsx(
          "border-t border-gray-700/50 bg-gray-800/50",
          isCompact ? "px-3 py-1.5" : "px-4 py-2"
        )}>
          {footer}
        </div>
      )}
    </div>
  );
}

// ── Mobile Detail Sheet ─────────────────────────────────────────────────

function MobileDetailSheet({ detailCallbacks }: { detailCallbacks?: DetailPaneCallbacks }) {
  const { selectItem } = useLibraryBrowser();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={() => selectItem(null)}
      />
      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 max-h-[70vh] bg-gray-800 border-t border-gray-700/50 rounded-t-xl overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-200">
        {/* Drag handle */}
        <div className="flex justify-center py-2 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-600" />
        </div>
        {/* Detail pane content */}
        <div className="flex-1 overflow-y-auto">
          <DetailPane callbacks={detailCallbacks} />
        </div>
      </div>
    </>
  );
}

// ── Mobile Section Selector ─────────────────────────────────────────────

function MobileSectionSelector({
  sections,
  activePath,
  navigateTo,
}: {
  sections: LibrarySection[];
  activePath: string[];
  navigateTo: (path: string[]) => void;
}) {
  const activeId = activePath[0] ?? null;

  // Group reference sections under a single "Newsquawk Library" pill
  const regularSections = sections.filter((s) => s.sectionVariant !== "reference");
  const referenceSections = sections.filter((s) => s.sectionVariant === "reference");
  const hasReference = referenceSections.length > 0;
  const isReferenceActive = referenceSections.some((s) => s.id === activeId);

  // For single reference section (e.g., nq-library from portfolio builder), navigate directly
  // For multiple reference sections (filter menus), navigate to the first one
  const handleReferenceClick = useCallback(() => {
    if (referenceSections.length === 1) {
      navigateTo([referenceSections[0].id]);
    } else if (referenceSections.length > 0) {
      navigateTo([referenceSections[0].id]);
    }
  }, [referenceSections, navigateTo]);

  return (
    <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-gray-700/30 bg-gray-800/30">
      {regularSections.map((section) => (
        <button
          key={section.id}
          onClick={() => navigateTo([section.id])}
          className={clsx(
            "px-2.5 py-1 text-2xs rounded-full whitespace-nowrap transition-colors focus:outline-none",
            activeId === section.id
              ? "bg-squawk-blue/20 text-squawk-blue border border-squawk-blue/30"
              : "bg-gray-800 text-gray-400 border border-gray-700/50 hover:text-gray-200 hover:border-gray-600"
          )}
        >
          {section.label}
        </button>
      ))}
      {hasReference && (
        <button
          onClick={handleReferenceClick}
          className={clsx(
            "px-2.5 py-1 text-2xs rounded-full whitespace-nowrap transition-colors focus:outline-none inline-flex items-center gap-1",
            isReferenceActive
              ? "bg-squawk-blue/20 text-squawk-blue border border-squawk-blue/30"
              : "bg-gray-800 text-gray-500 border border-gray-700/50 hover:text-gray-300 hover:border-gray-600"
          )}
        >
          <BookOpenIcon className="size-3" />
          Newsquawk
        </button>
      )}
    </div>
  );
}
